import React, {JSX, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BloodType, bloodTypeLabels, Sexuality} from "../../models/accountMedData";
import {upsertAccountMedData} from "../../api/accountMedDataApi";
import MessageBox, {MessageType} from "../components/MessageBox";
import {useRequiredAccount} from "../../hooks/AccountContext";
import {useSaveHandler} from "../../hooks/SaveHandlerContext";

interface MedicalFormData {
    birthDate: string;
    sex: Sexuality;
    bloodType: BloodType;
    meds: string;
    allergies: string;
    preExistingConditions: string;
    shareData: boolean;
}

interface MedicalDataProps {
}

function MedicalData(): JSX.Element {
    const {t} = useTranslation();

    const {
        account,
        medData: accountMedData,
        setMedData: setAccountMedData
    } = useRequiredAccount();

    const [formData, setFormData] = useState<MedicalFormData>({
        birthDate: "01.01.1990",
        sex: Sexuality.MALE,
        bloodType: BloodType.A_POSITIVE,
        meds: "",
        allergies: "",
        preExistingConditions: "",
        shareData: false,
    });

    // const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [message, setMessage] = useState<MessageType>(null);
    const [disableSaving, setDisableSaving] = useState<boolean>(true);

    const { setHasUnsavedChanges } = useSaveHandler();

    // Daten beim Mount laden
    useEffect(() => {
        if (!accountMedData) return;

        setFormData({
            birthDate: accountMedData.birthDate ?? "",
            sex: accountMedData.sex ?? Sexuality.MALE,
            bloodType: accountMedData.bloodType ?? BloodType.A_POSITIVE,
            meds: accountMedData.meds ?? "",
            allergies: accountMedData.allergies ?? "",
            preExistingConditions: accountMedData.preExistingConditions ?? "",
            shareData: accountMedData.shareData ?? true,
        });
    }, [accountMedData]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const {name, value, type} = e.target;
        const isCheckbox = type === "checkbox";
        const checked = isCheckbox ? (e.target as HTMLInputElement).checked : undefined;

        setDisableSaving(false);
        setHasUnsavedChanges(true);

        setFormData({
            ...formData,
            [name]: isCheckbox ? checked : value,
        });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage(null);

        try {
            const dto = {
                birthDate: formData.birthDate,
                sex: formData.sex,
                bloodType: formData.bloodType,
                meds: formData.meds,
                allergies: formData.allergies,
                preExistingConditions: formData.preExistingConditions,
                shareData: formData.shareData,
            };

            var newMedData = await upsertAccountMedData(account.id, dto);
            setAccountMedData(newMedData);
            setMessage({type: "success", text: t("common.messages.success")});
            setDisableSaving(true);
            setHasUnsavedChanges(false);
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    return (
        <div>
            <div>
                <h2>{t("menu.medical")}</h2>
            </div>

            <form onSubmit={handleSubmit} className="form-container">
                <MessageBox message={message}/>

                <div className="form-group">
                    <label>{t("contact.fields.birthDate")}</label>
                    <input
                        type="date"
                        name="birthDate"
                        value={formData.birthDate ?? ""}
                        onChange={handleChange}
                        className="form-input"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>{t("contact.fields.sex")}</label>
                    <select
                        name="sex"
                        value={formData.sex ?? ""}
                        onChange={handleChange}
                        className="form-input"
                        required
                    >
                        {Object.values(Sexuality).map((value) => (
                            <option key={value} value={value}>
                                {t(`contact.fields.sex${value.charAt(0) + value.slice(1).toLowerCase()}`)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>{t("medical.fields.bloodType")}</label>
                    <select
                        name="bloodType"
                        value={formData.bloodType ?? ""}
                        onChange={handleChange}
                        className="form-input"
                    >
                        {Object.values(BloodType).map((value) => (
                            <option key={value} value={value}>
                                {bloodTypeLabels[value]}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>{t("medical.fields.meds")}</label>
                    <textarea
                        name="meds"
                        value={formData.meds}
                        onChange={handleChange}
                        className="form-textarea"
                    />
                </div>

                <div className="form-group">
                    <label>{t("medical.fields.allergies")}</label>
                    <textarea
                        name="allergies"
                        value={formData.allergies}
                        onChange={handleChange}
                        className="form-textarea"
                    />
                </div>

                <div className="form-group">
                    <label>{t("medical.fields.preExistingConditions")}</label>
                    <textarea
                        name="preExistingConditions"
                        value={formData.preExistingConditions}
                        onChange={handleChange}
                        className="form-textarea"
                    />
                </div>

                <label>
                    <input
                        type="checkbox"
                        name="shareData"
                        checked={formData.shareData}
                        onChange={handleChange}
                    />{" "}
                    {t("contact.fields.share")}
                </label>

                <button type="submit" className="form-button" disabled={disableSaving}>
                    {t("common.button.save")}
                </button>
            </form>
        </div>
    );
}

export default MedicalData;